import type { components } from '@connected/api-client/api.generated';
import type { LinkOptions, RegisteredRouter } from '@tanstack/react-router';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { Page } from '@connected/ui/features/layouts';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { RisoMeasurementForm } from '@/components/features/riso-measurement';
import { Loading } from '@/components/loading';
import { useDevice } from '@/hooks/queries/use-devices';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/devices/$distributionBoardDeviceId/resistance-measurements/',
)({
    component: Layout,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
    validateSearch: z.object({
        page: z.coerce.number().optional().default(1),
    }),
    pendingComponent: () => <Loading action="Aan het laden." />,
});

type linkType<
    TRouter extends RegisteredRouter = RegisteredRouter,
    TFrom extends string = string,
    TTo extends string = '',
> = Omit<LinkOptions<TRouter, TFrom, TTo>, 'search'>;

type deviceProps = {
    [key in components['schemas']['ElectricalProjectDistributionBoardDeviceKind']]: {
        to: linkType['to'];
        text: string;
    }
};

const devices: deviceProps = {
    FinalCircuitProtectiveDevice: {
        to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/final-circuit-protective-devices',
        text: 'Eindgroepen',
    },
    ResidualCurrentCircuitBreakerDevice: {
        to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/residual-current-circuit-breakers',
        text: 'Aardlekautomaten',
    },
    ResidualCurrentDevice: {
        to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/residual-current-devices',
        text: 'Aardlekschakelaars',
    },
    IsolationResistanceMeasurement: {
        to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/information',
        text: 'Insolation resistance',
    },
};

function Layout() {
    const { t: c } = useTranslation('common');
    const params = Route.useParams();
    const search = Route.useSearch();

    const { data: { device } } = useDevice({
        electricalProjectVersionId: params.projectVersionId,
        distributionBoardDeviceId: params.distributionBoardDeviceId,
    });

    if (!device)
        return null;

    return (
        <Page>
            <Page.Header>
                <Page.Header.Bar>
                    <Page.Header.Back
                        to={devices[device.DeviceKind!].to}
                        params={params}
                        search={search}
                    />
                    <Page.Header.Title>Weerstandmetingen</Page.Header.Title>
                </Page.Header.Bar>
            </Page.Header>

            <RisoMeasurementForm
                electricalProjectVersionId={params.projectVersionId}
                distributionBoardDeviceId={params.distributionBoardDeviceId}
            />

            <footer className="flex items-end justify-end gap-4 p-4">
                <Button variant="outline" asChild>
                    <Link
                        to={devices[device.DeviceKind!].to}
                        params={params}
                        search={search}
                    >
                        {c('actions.general.back')}
                    </Link>
                </Button>
            </footer>
        </Page>
    );
}
